import { FocusedFeatureTypes } from 'common/types'
import { useAuthenticatedBeeldayClient } from 'connectivity/beelday-hooks'
import { AudioStreamingTrainer } from 'features/audio-streaming/audio-streaming-trainer'
import { YouTubeStreamingTrainer } from 'features/youtube-streaming/youtube-streaming-trainer'
import ConfiguredKnowledgeCheckViewTrainer from 'features/knowledge-checks/configured-knowledge-check-view-trainer'
import { ConfiguredNotepad } from 'features/notepad'
import ListPolling from 'features/pollings/list-polling/list-polling'
import ConfiguredMultipleChoicePolling from 'features/pollings/multiple-choice-polling/configured-multiple-choice-polling'
import WordCloud from 'features/pollings/word-cloud/word-cloud'
import { ConfiguredScreenShare } from 'features/screen-share'
import { SimpleGroupingFeature } from 'features/simple-grouping'
import { ConfiguredSlideEditor } from 'features/slides'
import { ConfiguredWhiteboard } from 'features/whiteboard'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { ErrorWithStreams } from 'room/common/error-with-streams'
import { useJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { SceneVideoUser } from 'video-conference-media'
import ConfiguredIceBreakerTrainer from './ice-breaker/configured-ice-breaker-trainer'
import { selectFocusedFeature } from './redux'
import { ConfiguredMediaRemote } from 'presenter-mode/configured-media-remote'
import { ConfiguredVideoMonitor } from 'presenter-mode/configured-video-monitor'
import { useStageSurface } from 'room/common/stage-surface'

type Props = {
	streams: SceneVideoUser[]
}
export const TrainerFocusedFeature: React.FC<Props> = ({ streams }) => {
	const focusedFeature = useSelector(selectFocusedFeature)
	const beeldayClient = useAuthenticatedBeeldayClient()
	const roomAddress = useJoinedRoomAddress()
	// While presenting, the players - and the sound - are in the presentation
	// window; the console gets a remote for a recording and a monitor for a film.
	const mediaHere = useStageSurface() !== 'console'

	const closeFocusedFeature = useCallback(() => {
		//TODO: implement other features
		if (!roomAddress) return
		switch (focusedFeature.type) {
			case FocusedFeatureTypes.GROUPING:
				beeldayClient.cancelGroupSuggestion(roomAddress)
				break
		}
	}, [beeldayClient, focusedFeature, roomAddress])

	return (
		<>
			{(() => {
				switch (focusedFeature.type) {
					case FocusedFeatureTypes.ICEBREAKER:
						return <ConfiguredIceBreakerTrainer />
					case FocusedFeatureTypes.KNOWLEDGE_CHECK:
						return (
							<ConfiguredKnowledgeCheckViewTrainer
								knowledgeCheckId={focusedFeature.knowledgeCheckId}
							/>
						)
					case FocusedFeatureTypes.ScreenShare:
						return <ConfiguredScreenShare streams={streams} />
					case FocusedFeatureTypes.SLIDE:
						return <ConfiguredSlideEditor />
					case FocusedFeatureTypes.POLLING_OPEN_ENDED_WORD_CLOUD:
						return <WordCloud isTrainer={true} streams={streams} />
					case FocusedFeatureTypes.POLLING_OPEN_ENDED_LIST:
						return <ListPolling isTrainer={true} streams={streams} />
					case FocusedFeatureTypes.POLLING_MULTIPLE_CHOICE:
						return (
							<ConfiguredMultipleChoicePolling
								isTrainer={true}
								streams={streams}
							/>
						)
					case FocusedFeatureTypes.WHITEBOARD:
						return <ConfiguredWhiteboard isTrainer={true} streams={streams} />
					case FocusedFeatureTypes.NOTEPAD:
						return <ConfiguredNotepad isTrainer={true} streams={streams} />
					case FocusedFeatureTypes.SIMPLE_GROUPING:
						return <SimpleGroupingFeature />
					case FocusedFeatureTypes.AUDIO_STREAMING:
						return mediaHere ? (
							<AudioStreamingTrainer streams={streams} />
						) : (
							<ConfiguredMediaRemote />
						)
					case FocusedFeatureTypes.YOUTUBE_STREAMING:
						return mediaHere ? (
							<YouTubeStreamingTrainer streams={streams} />
						) : (
							<ConfiguredVideoMonitor />
						)
					default:
						return <ErrorWithStreams retry={closeFocusedFeature} />
				}
			})()}
		</>
	)
}
